/**
 * Serviço global de notificação sonora para nova mensagem no Inbox.
 * Respeita autoplay do navegador, preferências do usuário e regras de quando tocar.
 */
import { Injectable } from '@angular/core';

const STORAGE_KEY_ENABLED = 'pipeup_notifications_sound_enabled';
const STORAGE_KEY_VOLUME = 'pipeup_notifications_sound_volume';
const DEFAULT_VOLUME = 0.6;

const SOUND_URL = 'assets/sounds/new-message.mp3';

@Injectable({ providedIn: 'root' })
export class InboxSoundService {
  private audio: HTMLAudioElement | null = null;
  private enabled = true;
  private volume = DEFAULT_VOLUME;
  private unlocked = false;
  private soundLoadFailed = false;

  constructor() {
    // Não criar Audio no construtor para evitar GET/404 ao carregar a página.
    // O áudio é criado na primeira vez que play() ou unlock() for chamado.
  }

  private getOrCreateAudio(): HTMLAudioElement | null {
    if (this.soundLoadFailed) return null;
    if (this.audio) return this.audio;
    if (typeof document === 'undefined') return null;
    const base = document.querySelector('base')?.getAttribute('href')?.replace(/\/$/, '') ?? '';
    const audio = new Audio(`${base}${SOUND_URL}`);
    audio.volume = this.volume;
    audio.addEventListener('error', () => {
      this.soundLoadFailed = true;
      this.audio = null;
    });
    this.audio = audio;
    return audio;
  }

  /**
   * Carregar preferências do localStorage e aplicar.
   * Chamar ao iniciar a aplicação.
   */
  init(): void {
    try {
      const storedEnabled = localStorage.getItem(STORAGE_KEY_ENABLED);
      if (storedEnabled !== null) {
        this.enabled = storedEnabled === 'true';
      }
      const storedVolume = localStorage.getItem(STORAGE_KEY_VOLUME);
      if (storedVolume !== null) {
        const v = parseFloat(storedVolume);
        if (!Number.isNaN(v) && v >= 0 && v <= 1) {
          this.volume = v;
          if (this.audio) this.audio.volume = v;
        }
      }
    } catch {
      // Ignorar erros de localStorage
    }
  }

  /**
   * Chamar após a primeira interação do usuário (ex.: clique) para desbloquear áudio.
   * Toca um áudio silencioso uma vez para obter permissão do navegador.
   */
  unlockAudioOnUserInteraction(): void {
    if (this.unlocked) return;
    this.unlocked = true;
    const audio = this.getOrCreateAudio();
    if (!audio) return;
    try {
      audio.volume = 0;
      const p = audio.play();
      if (p && typeof p.then === 'function') {
        p.then(() => {
          audio.volume = this.volume;
          audio.currentTime = 0;
        }).catch(() => {
          audio.volume = this.volume;
        });
      } else {
        audio.volume = this.volume;
      }
    } catch {
      audio.volume = this.volume;
    }
  }

  /**
   * Tocar o som de nova mensagem (se habilitado e desbloqueado).
   * Não quebra a aplicação em caso de falha de permissão ou se o arquivo não existir (404).
   */
  play(): void {
    if (!this.enabled) return;
    const audio = this.getOrCreateAudio();
    if (!audio) return;
    try {
      audio.volume = this.volume;
      audio.currentTime = 0;
      const p = audio.play();
      if (p && typeof p.then === 'function') {
        p.catch(() => {
          // Autoplay bloqueado ou outro erro – silencioso
        });
      }
    } catch {
      // Ignorar erros de play
    }
  }

  setEnabled(value: boolean): void {
    this.enabled = value;
    try {
      localStorage.setItem(STORAGE_KEY_ENABLED, String(value));
    } catch {}
  }

  setVolume(value: number): void {
    const v = Math.max(0, Math.min(1, value));
    this.volume = v;
    if (this.audio) this.audio.volume = v;
    try {
      localStorage.setItem(STORAGE_KEY_VOLUME, String(v));
    } catch {}
  }

  getEnabled(): boolean {
    return this.enabled;
  }

  getVolume(): number {
    return this.volume;
  }
}
